/*
  Copyright 2018-2020 National Geographic Society

  Use of this software does not constitute endorsement by National Geographic
  Society (NGS). The NGS name and NGS logo may not be used for any purpose without
  written permission from NGS.

  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
// import Chip from '@material-ui/core/Chip'; // UNUSED - no Chip components in render
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import withStyles from '@material-ui/core/styles/withStyles';
import withTheme from '@material-ui/core/styles/withTheme';
// Add Material-UI icon imports
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import OpacityIcon from '@material-ui/icons/Opacity';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import IconButton from '@material-ui/core/IconButton';
import Slider from '@material-ui/core/Slider';
import Collapse from '@material-ui/core/Collapse';
import Typography from '@material-ui/core/Typography';
import Portal from '@material-ui/core/Portal';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';

import debounce from 'lodash/debounce';
import compose from 'lodash/fp/compose';
import React from 'react';
import { withTranslation } from 'react-i18next';
import { animated, Keyframes } from 'react-spring/renderprops.cjs';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';

import { Html } from '@marapp/earth-shared';
import { replace } from 'redux-first-router';

import { MAP_SIDEBAR_WIDTH, MAP_SIDEBAR_WIDTH_WIDE } from '../../../config';
import { IPopup } from '../../../modules/map/selectors';
import { AnalysisRoutes, EarthRoutes, IRouter } from '../../../modules/router/model';
import LegendItemGroup from './legend-item-group';
import TEMPLATES from './templates';

// Simplified Material-UI toolbar component without popup
const MaterialUILegendToolbar = ({
  layerGroup,
  onInfoClick,
  onVisibilityClick,
  onOpacityClick,
  onRemoveClick,
  onExpandClick,
  theme,
  isOpacityActive,
  hasLongLegend,
  isLegendExpanded,
  legendItemCount,
}) => {
  const mutedIconColor = 'rgba(255,255,255,0.6)'; // Muted white color
  const activeIconColor = 'rgba(255,255,255,0.8)'; // Slightly brighter for active states

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent={hasLongLegend ? 'space-between' : 'flex-end'}
      width="100%"
    >
      {/* Legend expand info on the left - only show if there's a long legend */}
      {hasLongLegend && (
        <Typography
          variant="caption"
          style={{
            color: 'rgba(255,255,255,0.7)',
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {legendItemCount} Categories
        </Typography>
      )}

      {/* Icons on the right */}
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        {/* Expand/Collapse Button for long legends */}
        {hasLongLegend && (
          <IconButton size="small" onClick={onExpandClick} style={{ color: mutedIconColor }}>
            {isLegendExpanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ExpandLessIcon fontSize="small" />
            )}
          </IconButton>
        )}

        {/* Info Button */}
        {layerGroup.description && (
          <IconButton size="small" onClick={onInfoClick} style={{ color: mutedIconColor }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        )}

        {/* Opacity Button */}
        <IconButton
          size="small"
          onClick={onOpacityClick}
          style={{
            color: isOpacityActive ? activeIconColor : mutedIconColor,
            backgroundColor: isOpacityActive ? 'rgba(255,255,255,0.1)' : 'transparent',
          }}
        >
          <OpacityIcon fontSize="small" />
        </IconButton>

        {/* Visibility Toggle Button */}
        <IconButton
          size="small"
          onClick={() => onVisibilityClick(layerGroup, !layerGroup.visibility)}
          style={{ color: layerGroup.visibility ? activeIconColor : mutedIconColor }}
        >
          {layerGroup.visibility ? (
            <VisibilityIcon fontSize="small" />
          ) : (
            <VisibilityOffIcon fontSize="small" />
          )}
        </IconButton>

        {/* Remove Button */}
        <IconButton size="small" onClick={onRemoveClick} style={{ color: mutedIconColor }}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

// Opacity Popup Component
const OpacityPopup = ({ open, onClose, opacity, onOpacityChange, anchorEl, theme }) => {
  if (!open || !anchorEl) return null;

  const rect = anchorEl.getBoundingClientRect();

  // Calculate popup dimensions
  const popupWidth = 180;
  const popupHeight = 80; // Approximate height

  // Get viewport dimensions
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Calculate optimal position
  let left = rect.left - 75;
  let top = rect.bottom + 8;

  // Adjust horizontal position if popup would go off-screen
  if (left < 10) {
    left = 10; // Minimum left margin
  } else if (left + popupWidth > viewportWidth - 10) {
    left = viewportWidth - popupWidth - 10; // Adjust to fit within viewport
  }

  // Adjust vertical position if popup would go off-screen
  if (top + popupHeight > viewportHeight - 10) {
    top = rect.top - popupHeight - 8; // Show above the button instead
  }

  // Ensure minimum top margin
  if (top < 10) {
    top = 10;
  }

  return (
    <Portal>
      {/* Invisible overlay to close popup when clicking outside */}
      <Box position="fixed" top={0} left={0} right={0} bottom={0} zIndex={9999} onClick={onClose} />
      <Box
        position="fixed"
        style={{
          top,
          left,
          zIndex: 10000,
        }}
        bgcolor="#455A64"
        p={1}
        borderRadius={1}
        boxShadow={theme.shadows[16]}
        minWidth={140}
        maxWidth={180}
        onClick={(e) => e.stopPropagation()}
        border={`1px solid ${theme.palette.divider}`}
      >
        <Typography
          variant="caption"
          style={{
            color: theme.palette.common.white,
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: '10px',
            marginBottom: theme.spacing(0.5),
            display: 'block',
            textAlign: 'center',
            letterSpacing: '0.5px',
          }}
        >
          Opacity: {Math.round(opacity * 100)}%
        </Typography>

        <Box mb={0.5}>
          <Slider
            value={opacity}
            onChange={onOpacityChange}
            min={0}
            max={1}
            step={0.05}
            style={{
              color: '#E0E0E0',
              height: 3,
              margin: '6px 0',
            }}
          />
        </Box>

        {/* 0-100 labels positioned under the slider */}
        <Box display="flex" justifyContent="space-between" px={0.5} mt={-0.5}>
          <Typography
            variant="caption"
            style={{
              color: 'rgba(255,255,255,0.7)',
              fontSize: '8px',
              fontWeight: 400,
              lineHeight: 1,
            }}
          >
            0%
          </Typography>
          <Typography
            variant="caption"
            style={{
              color: 'rgba(255,255,255,0.7)',
              fontSize: '8px',
              fontWeight: 400,
              lineHeight: 1,
            }}
          >
            100%
          </Typography>
        </Box>
      </Box>
    </Portal>
  );
};

// Creates a spring with predefined animation slots
const LegendWrapper: any = Keyframes.Spring({
  open: { x: MAP_SIDEBAR_WIDTH, opacity: 1, delay: 0 },
  openW: { x: MAP_SIDEBAR_WIDTH_WIDE, opacity: 1 },
  close: { x: 0, opacity: 1, delay: 100 },
});

import { Theme, createStyles } from '@material-ui/core/styles';

const styles = (theme: Theme) =>
  createStyles({
    '@global': {
      '.rc-tooltip': {
        color: theme.palette.text.primary,
        '& .rc-tooltip-inner': {
          backgroundColor: '#000',
          boxShadow: 'none',
        },
        '& .rc-tooltip-arrow': {
          borderTopColor: '#000',
        },
        '& .rc-slider-mark': {
          left: 0,
          width: '100%',
        },
        '& *': {
          color: '#fff',
        },
      },
    },
    root: {
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[1],
      position: 'fixed',
      left: theme.spacing(2),
      bottom: theme.spacing(2),
      right: '0px',
      width: 300,
      zIndex: theme.zIndex.drawer,
      overflow: 'hidden',
    },
  });

export interface ILegend {
  classes?: any;
  layerGroups?: any[];
  setLayerVisibility?: (data: any) => void;
  setLayerOrder?: (data: any) => void;
  setLayerOpacity?: (data: any) => void;
  setLayerGroupCurrent?: (data: any) => void;
  toggleLayer?: any;
  setLayerSettings?: (data: any) => void;
  open?: boolean;
  router?: IRouter;
  t?: (arg: any) => any;
  theme?: any;
  popup?: IPopup;
  // Add these props
  updateUrlWithLayers?: boolean; // Optional flag to enable URL updates
}

// Move CustomLegendHeader outside the class, before the class definition
const CustomLegendHeader = ({ title, expanded, onToggle, theme }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    p={1}
    pl={1.5} // Add more left padding
    borderBottom={`1px solid ${theme.palette.divider}`}
    bgcolor="#455A64"
  >
    <Typography variant="body2" style={{ textTransform: 'uppercase' }}>
      {title}
    </Typography>
    <IconButton size="small" onClick={onToggle}>
      {expanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
    </IconButton>
  </Box>
);

class LegendComponent extends React.PureComponent<ILegend & IPopup, any> {
  public legendRef: any;
  // COMMENTED OUT - unused with new implementation
  // private opacityButtonRefs: Map<string, HTMLElement> = new Map();
  private draggedItem: any = null;
  // private draggedOverItem: any = null; // COMMENTED OUT - set but never read

  public onChangeOpacity = debounce((l, opacity, slug) => {
    const { setLayerOpacity } = this.props;
    setLayerOpacity({ slug, dataset: { id: l.dataset }, opacity });
  }, 250);

  public onRemoveLayer = debounce((layer) => {
    const { toggleLayer, updateUrlWithLayers, layerGroups } = this.props;

    // Update URL before removing if enabled
    if (updateUrlWithLayers) {
      // Calculate new layer order without the removed layer
      const remainingVisibleLayers = layerGroups
        .filter((lg) => lg.visibility && lg.slug !== layer.slug)
        .map((lg) => lg.dataset);

      this.updateUrlWithLayerOrder(remainingVisibleLayers);
    }

    toggleLayer(layer);
  }, 250);

  public constructor(props: any) {
    super(props);
    this.legendRef = React.createRef();
    this.state = {
      expanded: false, // Changed from true to false - default to collapsed
      infoDialog: {
        open: false,
        layerGroup: null,
      },
      opacityPopup: {
        open: false,
        layerSlug: null,
        opacity: 1,
        anchorEl: null,
      },
      draggedOverIndex: null,
      expandedLegends: new Set(),
      // Add flag to prevent infinite updates
      isUpdatingFromUrl: false,
    };
  }

  // Add method to toggle legend expansion for a specific layer
  public toggleLegendExpansion = (layerSlug: string) => {
    this.setState((prevState) => {
      const expandedLegends = new Set(prevState.expandedLegends);
      if (expandedLegends.has(layerSlug)) {
        expandedLegends.delete(layerSlug);
      } else {
        expandedLegends.add(layerSlug);
      }
      return { expandedLegends };
    });
  };

  // Add method to check if a layer has many legend items
  public hasLongLegend = (layerGroup: any): boolean => {
    const LONG_LEGEND_THRESHOLD = 5; // Adjust this threshold as needed

    const activeLayer = layerGroup.layers?.find((l) => l.active) || layerGroup.layers?.[0];

    // Check various legend data structures for item count
    if (activeLayer?.legendConfig?.items?.length > LONG_LEGEND_THRESHOLD) return true;
    if (layerGroup.legend?.items?.length > LONG_LEGEND_THRESHOLD) return true;
    if (layerGroup.legend?.config?.items?.length > LONG_LEGEND_THRESHOLD) return true;
    if (Array.isArray(layerGroup.legend) && layerGroup.legend.length > LONG_LEGEND_THRESHOLD)
      return true;
    if (Array.isArray(activeLayer?.legend) && activeLayer.legend.length > LONG_LEGEND_THRESHOLD)
      return true;
    if (activeLayer?.legend?.items?.length > LONG_LEGEND_THRESHOLD) return true;

    return false;
  };

  // Add method to count legend items
  public getLegendItemCount = (layerGroup: any): number => {
    const activeLayer = layerGroup.layers?.find((l) => l.active) || layerGroup.layers?.[0];

    if (activeLayer?.legendConfig?.items?.length) return activeLayer.legendConfig.items.length;
    if (layerGroup.legend?.items?.length) return layerGroup.legend.items.length;
    if (layerGroup.legend?.config?.items?.length) return layerGroup.legend.config.items.length;
    if (Array.isArray(layerGroup.legend)) return layerGroup.legend.length;
    if (Array.isArray(activeLayer?.legend)) return activeLayer.legend.length;
    if (activeLayer?.legend?.items?.length) return activeLayer.legend.items.length;

    return 0;
  };

  // Add drag and drop handlers
  public handleDragStart = (e: React.DragEvent, layerGroup: any, index: number) => {
    e.stopPropagation(); // Prevent bubbling to map
    this.draggedItem = { layerGroup, index };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', 'legend-layer'); // Add identifier
  };

  public handleDragEnd = (e?: React.DragEvent) => {
    if (e) e.stopPropagation(); // Prevent bubbling to map
    this.draggedItem = null;
    // this.draggedOverItem = null; // COMMENTED OUT - unused
    this.setState({ draggedOverIndex: null });
  };

  public handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent bubbling to map
    e.dataTransfer.dropEffect = 'move';
    this.setState({ draggedOverIndex: index });
  };

  public handleDragLeave = (e: React.DragEvent) => {
    e.stopPropagation(); // Prevent bubbling to map
    this.setState({ draggedOverIndex: null });
  };

  public handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent bubbling to map

    if (this.draggedItem && this.draggedItem.index !== dropIndex) {
      const { layerGroups } = this.props;
      const draggedLayerGroup = this.draggedItem.layerGroup;
      const draggedIndex = this.draggedItem.index;

      // Create new order array
      const newOrder = [...layerGroups];
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(dropIndex, 0, draggedLayerGroup);

      // Create array of dataset IDs in new order
      const datasetIds = newOrder.map((lg) => lg.dataset);

      // Call the order change handler
      this.onChangeOrder(datasetIds);
    }

    this.setState({ draggedOverIndex: null });
  };

  // COMMENTED OUT - unused with new implementation
  /*
  public UNSAFE_componentWillReceiveProps(nextProps: Readonly<ILegend>) {
    const popupKeys = Object.keys(nextProps.popup);
    if (popupKeys && popupKeys.length && this.legendRef) {
      this.legendRef.current?.setState((prevState: any) => ({ ...prevState, expanded: false }));
    }
  }
  */

  public onChangeInfo = (info, slug) => {
    const { layerGroups } = this.props;
    const layerGroup = layerGroups.find((lg) => lg.slug === slug);

    this.setState({
      infoDialog: {
        open: info,
        layerGroup: info ? layerGroup : null,
      },
    });
  };

  public handleCloseInfoDialog = () => {
    this.setState({
      infoDialog: {
        open: false,
        layerGroup: null,
      },
    });
  };

  public onChangeVisibility = (l, visibility, slug) => {
    const { setLayerVisibility, updateUrlWithLayers, layerGroups } = this.props;

    // Update layer visibility in state
    setLayerVisibility({ slug, dataset: { id: l.dataset }, visibility });

    // Update URL if enabled
    if (updateUrlWithLayers) {
      // Use setTimeout to ensure state is updated first
      setTimeout(() => {
        const updatedLayerGroups = layerGroups.map((lg) =>
          lg.slug === slug ? { ...lg, visibility } : lg
        );

        // Create layer string with 0 opacity for hidden layers
        const layerString = updatedLayerGroups
          .filter((lg) => lg.visibility || lg.slug === slug) // Include the layer being toggled
          .map((layerGroup) => {
            const opacity =
              layerGroup.slug === slug && !visibility
                ? 0 // Set opacity to 0 for newly hidden layer
                : Math.round((layerGroup.opacity || 1) * 100);

            return `${layerGroup.slug}_${opacity}`;
          })
          .join(',');

        // Get current URL params and update layers
        const currentParams = new URLSearchParams(window.location.search);

        if (layerString) {
          currentParams.set('layers', layerString);
        } else {
          currentParams.delete('layers');
        }

        // Build new URL manually to preserve commas
        const params = Array.from(currentParams.entries());
        const queryString = params
          .map(([key, value]) => {
            if (key === 'layers') {
              return `${key}=${layerString}`;
            }
            return `${key}=${encodeURIComponent(value)}`;
          })
          .join('&');

        const newSearch = queryString ? `?${queryString}` : '';
        const newUrl = `${window.location.pathname}${newSearch}`;

        // Update URL without page reload
        replace(newUrl);
      }, 100); // Small delay to ensure state update
    }
  };

  public onChangeOrder = (datasetIds) => {
    const { setLayerOrder, layerGroups, updateUrlWithLayers } = this.props;

    // Update the layer order in state
    setLayerOrder({ datasetIds });

    // Update URL if enabled
    if (updateUrlWithLayers) {
      this.updateUrlWithLayerOrder(datasetIds);
    }
  };

  // Add new method to update URL with layer order
  public updateUrlWithLayerOrder = (datasetIds: string[]) => {
    const { layerGroups } = this.props;

    // Create layer string with opacity info (similar to handleClick logic)
    const layerString = datasetIds
      .map((datasetId) => {
        const layerGroup = layerGroups.find((lg) => lg.dataset === datasetId);
        if (!layerGroup || !layerGroup.visibility) return null;

        const opacity = Math.round((layerGroup.opacity || 1) * 100);
        return `${layerGroup.slug}_${opacity}`;
      })
      .filter(Boolean)
      .join(',');

    // Get current URL params and update layers
    const currentParams = new URLSearchParams(window.location.search);

    if (layerString) {
      currentParams.set('layers', layerString);
    } else {
      currentParams.delete('layers');
    }

    // Build new URL manually to preserve commas
    const params = Array.from(currentParams.entries());
    const queryString = params
      .map(([key, value]) => {
        if (key === 'layers') {
          return `${key}=${layerString}`; // Don't encode the layer string
        }
        return `${key}=${encodeURIComponent(value)}`;
      })
      .join('&');

    const newSearch = queryString ? `?${queryString}` : '';
    const newUrl = `${window.location.pathname}${newSearch}`;

    // Update URL without page reload
    replace(newUrl);
  };

  public onChangeCurrent = (l, current, slug) => {
    const { setLayerGroupCurrent } = this.props;

    setLayerGroupCurrent({ slug, current });
  };

  public onChangeLayerDate = (dates, layer) => {
    const { setLayerSettings } = this.props;
    const { slug, decodeConfig } = layer;

    setLayerSettings({
      slug,
      settings: {
        ...(decodeConfig && {
          decodeParams: {
            startDate: dates[0],
            endDate: dates[1],
            trimEndDate: dates[2],
          },
        }),
        ...(!decodeConfig && {
          params: {
            startDate: dates[0],
            endDate: dates[1],
          },
        }),
      },
    });
  };

  public getState = () => {
    const { open, router } = this.props;
    const wideRoutes: (EarthRoutes | AnalysisRoutes)[] = [
      EarthRoutes.LOCATION,
      EarthRoutes.COLLECTION,
      AnalysisRoutes.ELSA_NEW,
      AnalysisRoutes.ELSA_EDIT,
    ];
    const selectedOpen = wideRoutes.includes(router.type);

    if (open) {
      if (selectedOpen) {
        return 'openW';
      }
      return 'open';
    } else {
      return 'close';
    }
  };

  public handleOpacityClick = (layerGroup, event) => {
    event.stopPropagation();
    const currentlyOpen =
      this.state.opacityPopup.open && this.state.opacityPopup.layerSlug === layerGroup.slug;

    this.setState({
      opacityPopup: {
        open: !currentlyOpen,
        layerSlug: currentlyOpen ? null : layerGroup.slug,
        opacity: layerGroup.opacity || 1,
        anchorEl: currentlyOpen ? null : event.currentTarget,
      },
    });
  };

  public handleOpacityClose = () => {
    const { updateUrlWithLayers, layerGroups } = this.props;
    const { opacityPopup } = this.state;

    // Update URL after a short delay when popup closes
    if (updateUrlWithLayers && opacityPopup.layerSlug) {
      setTimeout(() => {
        const visibleLayers = layerGroups.filter((lg) => lg.visibility).map((lg) => lg.dataset);

        this.updateUrlWithLayerOrder(visibleLayers);
      }, 300); // 300ms delay
    }

    this.setState({
      opacityPopup: {
        open: false,
        layerSlug: null,
        opacity: 1,
        anchorEl: null,
      },
    });
  };

  public handleOpacityChange = (event, newValue) => {
    const { opacityPopup } = this.state;
    const { layerGroups } = this.props;

    const layerGroup = layerGroups.find((lg) => lg.slug === opacityPopup.layerSlug);
    if (layerGroup) {
      this.setState({
        opacityPopup: {
          ...opacityPopup,
          opacity: newValue,
        },
      });
      this.onChangeOpacity(layerGroup, newValue, layerGroup.slug);
    }
  };

  public componentDidMount() {
    // Listen for browser back/forward button and manual URL changes
    window.addEventListener('popstate', this.handleUrlChange);

    // Also check URL on initial mount
    this.handleUrlChange();
  }

  public componentWillUnmount() {
    window.removeEventListener('popstate', this.handleUrlChange);
  }

  private handleUrlChange = () => {
    const { updateUrlWithLayers, layerGroups } = this.props;

    if (!updateUrlWithLayers) return;

    // Parse current URL parameters
    const currentParams = new URLSearchParams(window.location.search);
    const layersParam = currentParams.get('layers');

    if (layersParam) {
      this.syncLegendWithUrl(layersParam);
    } else {
      // No layers in URL - hide all layers
      this.hideAllLayers();
    }
  };

  private syncLegendWithUrl = (layersParam: string) => {
    const { layerGroups, setLayerVisibility, setLayerOpacity } = this.props;

    // Parse layer parameter: "layer1_80,layer2_100,layer3_0"
    const urlLayers = layersParam.split(',').map((layerStr) => {
      const slug = layerStr.replace(/_\d{1,3}$/, '');
      const match = layerStr.match(/_(\d{1,3})$/);
      const opacity = match ? Number(match[1]) / 100 : 1;
      return { slug, opacity, visible: opacity > 0 };
    });

    // Update each layer group to match URL
    layerGroups.forEach((layerGroup) => {
      const urlLayer = urlLayers.find((ul) => ul.slug === layerGroup.slug);

      if (urlLayer) {
        // Layer is in URL - set visibility and opacity
        if (layerGroup.visibility !== urlLayer.visible) {
          setLayerVisibility({
            slug: layerGroup.slug,
            dataset: { id: layerGroup.dataset },
            visibility: urlLayer.visible,
          });
        }

        if (Math.abs((layerGroup.opacity || 1) - urlLayer.opacity) > 0.01) {
          setLayerOpacity({
            slug: layerGroup.slug,
            dataset: { id: layerGroup.dataset },
            opacity: urlLayer.opacity,
          });
        }
      } else {
        // Layer not in URL - hide it
        if (layerGroup.visibility) {
          setLayerVisibility({
            slug: layerGroup.slug,
            dataset: { id: layerGroup.dataset },
            visibility: false,
          });
        }
      }
    });
  };

  private hideAllLayers = () => {
    const { layerGroups, setLayerVisibility } = this.props;

    layerGroups.forEach((layerGroup) => {
      if (layerGroup.visibility) {
        setLayerVisibility({
          slug: layerGroup.slug,
          dataset: { id: layerGroup.dataset },
          visibility: false,
        });
      }
    });
  };

  public render() {
    const { classes, layerGroups, t, theme } = this.props;
    const state = this.getState();
    const { expanded, infoDialog, opacityPopup, draggedOverIndex, expandedLegends } = this.state;

    // Check if there are any layers at all, not just visible ones
    const hasAnyLayers = layerGroups?.length > 0;

    return (
      <>
        <LegendWrapper native={true} state={state}>
          {({ x, ...props }) => (
            <animated.div
              className={`${classes.root} marapp-qa-legend`}
              style={{
                transform: x.interpolate((x) => `translate3d(${x}px,0,0)`),
                ...props,
              }}
            >
              <Box bgcolor="#455A64">
                <CustomLegendHeader
                  title={t('Legend')}
                  expanded={expanded}
                  onToggle={() => this.setState({ expanded: !expanded })}
                  theme={theme}
                />
                <Collapse in={expanded && hasAnyLayers}>
                  <Box maxHeight="65vh" overflow="auto" bgcolor="#455A64">
                    <List>
                      {layerGroups
                        // Remove the visibility filter to show all layers
                        ?.map((layerGroup: any, i) => {
                          const hasLongLegend = this.hasLongLegend(layerGroup);
                          const legendItemCount = this.getLegendItemCount(layerGroup);
                          const isLegendExpanded = expandedLegends.has(layerGroup.slug);

                          return (
                            <React.Fragment key={layerGroup.slug}>
                              <ListItem
                                alignItems="flex-start"
                                style={{
                                  flexDirection: 'column',
                                  alignItems: 'stretch',
                                  backgroundColor:
                                    draggedOverIndex === i
                                      ? 'rgba(255,255,255,0.1)'
                                      : 'transparent',
                                  transition: 'background-color 0.2s ease',
                                  paddingTop: theme.spacing(1),
                                  paddingBottom: theme.spacing(0.5),
                                  cursor: 'pointer',
                                  // Add visual indication for hidden layers
                                  opacity: layerGroup.visibility ? 1 : 0.5,
                                  '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                  },
                                }}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                  },
                                }}
                                draggable
                                onDragStart={(e) => this.handleDragStart(e, layerGroup, i)}
                                onDragEnd={this.handleDragEnd}
                                onDragOver={(e) => this.handleDragOver(e, i)}
                                onDragLeave={this.handleDragLeave}
                                onDrop={(e) => this.handleDrop(e, i)}
                              >
                                {/* Layer Header with Name and Toolbar */}
                                <Box display="flex" flexDirection="column" width="100%" mb={0}>
                                  {/* Layer Name with Drag Indicator */}
                                  <Box
                                    display="flex"
                                    alignItems="flex-start"
                                    mb={theme.spacing(0.125)} // Reduced from 0.25
                                  >
                                    {/* Drag Indicator */}
                                    <DragIndicatorIcon
                                      fontSize="small"
                                      style={{
                                        color: 'rgba(255,255,255,0.4)',
                                        marginRight: theme.spacing(0.25),
                                        marginTop: '1px',
                                        cursor: 'grab',
                                      }}
                                    />

                                    {/* Layer Name */}
                                    <Typography
                                      variant="body2"
                                      style={{
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word',
                                        hyphens: 'auto',
                                        color: theme.palette.common.white,
                                        fontSize: '13px',
                                        lineHeight: 1.3,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        flex: 1,
                                      }}
                                      title={layerGroup.name}
                                    >
                                      {layerGroup.name}
                                    </Typography>
                                  </Box>

                                  {/* Toolbar Icons - Always Right Aligned */}
                                  <Box display="flex" justifyContent="flex-end" width="100%">
                                    <MaterialUILegendToolbar
                                      layerGroup={layerGroup}
                                      onInfoClick={() => this.onChangeInfo(true, layerGroup.slug)}
                                      onVisibilityClick={(lg, visibility) =>
                                        this.onChangeVisibility(lg, visibility, lg.slug)
                                      }
                                      onOpacityClick={(event) =>
                                        this.handleOpacityClick(layerGroup, event)
                                      }
                                      onRemoveClick={() => this.onRemoveLayer(layerGroup)}
                                      onExpandClick={() =>
                                        this.toggleLegendExpansion(layerGroup.slug)
                                      }
                                      theme={theme}
                                      isOpacityActive={
                                        opacityPopup.open &&
                                        opacityPopup.layerSlug === layerGroup.slug
                                      }
                                      hasLongLegend={hasLongLegend}
                                      isLegendExpanded={isLegendExpanded}
                                      legendItemCount={legendItemCount}
                                    />
                                  </Box>
                                </Box>

                                {/* Layer Legend Content - Always render container */}
                                <Box
                                  pl={1}
                                  pr={1}
                                  pb={0.5}
                                  pt={0.25}
                                  style={{
                                    visibility: layerGroup.visibility ? 'visible' : 'hidden',
                                    height: layerGroup.visibility ? 'auto' : 0,
                                    overflow: 'hidden',
                                  }}
                                >
                                  {/* Legend content with conditional collapse */}
                                  <Collapse in={!hasLongLegend || isLegendExpanded}>
                                    {(() => {
                                      // Debug logging to understand the data structure
                                      // console.log('=== Legend Debug Info ===');
                                      // console.log('LayerGroup:', layerGroup);
                                      // console.log('LayerGroup.layers:', layerGroup.layers);

                                      const activeLayer =
                                        layerGroup.layers?.find((l) => l.active) ||
                                        layerGroup.layers?.[0];
                                      // console.log('Active layer:', activeLayer);
                                      // console.log('Active layer source:', activeLayer?.source);
                                      // console.log(
                                      //   'Active layer source type:',
                                      //   activeLayer?.source?.type
                                      // );
                                      // console.log(
                                      //   'Active layer legendConfig:',
                                      //   activeLayer?.legendConfig
                                      // );
                                      // console.log('=========================');

                                      // Try to use LegendItemGroup for both group and raster types
                                      if (activeLayer && activeLayer.source) {
                                        const sourceType = activeLayer.source.type;

                                        if (
                                          sourceType === 'group' ||
                                          (sourceType === 'raster' && activeLayer.legendConfig)
                                        ) {
                                          try {
                                            // console.log(
                                            //   'Attempting to render with LegendItemGroup'
                                            // );
                                            return (
                                              <LegendItemGroup
                                                index={i}
                                                layerGroup={layerGroup}
                                                templates={TEMPLATES}
                                                onChangeInfo={this.onChangeInfo}
                                                onChangeOpacity={(activeLayer, value) =>
                                                  this.onChangeOpacity(
                                                    activeLayer,
                                                    value,
                                                    activeLayer.slug
                                                  )
                                                }
                                                onChangeVisibility={(activeLayer, value) =>
                                                  this.onChangeVisibility(
                                                    activeLayer,
                                                    value,
                                                    activeLayer.slug
                                                  )
                                                }
                                                onChangeOrder={this.onChangeOrder}
                                                onChangeCurrent={(activeLayer, value) =>
                                                  this.onChangeCurrent(
                                                    activeLayer,
                                                    value,
                                                    activeLayer.slug
                                                  )
                                                }
                                                onChangeLayerDate={this.onChangeLayerDate}
                                              />
                                            );
                                          } catch (error) {
                                            console.error(
                                              'LegendItemGroup rendering error:',
                                              error
                                            );
                                          }
                                        }
                                      }

                                      // Check for legendConfig first (fallback if LegendItemGroup fails)
                                      if (
                                        activeLayer?.legendConfig?.items &&
                                        Array.isArray(activeLayer.legendConfig.items)
                                      ) {
                                        console.log(
                                          'Using activeLayer.legendConfig.items as fallback'
                                        );
                                        return activeLayer.legendConfig.items.map((item, idx) => (
                                          <Box
                                            key={idx}
                                            display="flex"
                                            alignItems="center"
                                            mb={0.5}
                                          >
                                            <Box
                                              width={16}
                                              height={16}
                                              mr={1}
                                              style={{
                                                backgroundColor: item.color,
                                                borderRadius: 2,
                                                border: '1px solid rgba(255,255,255,0.2)',
                                              }}
                                            />
                                            <Typography
                                              variant="caption"
                                              style={{
                                                color: theme.palette.common.white,
                                                fontSize: '11px',
                                              }}
                                            >
                                              {item.name}
                                            </Typography>
                                          </Box>
                                        ));
                                      }

                                      // Fallback rendering for different legend structures
                                      const renderLegendItems = (items) => {
                                        if (!items || !Array.isArray(items)) return null;

                                        return items.map((item, idx) => (
                                          <Box
                                            key={idx}
                                            display="flex"
                                            alignItems="center"
                                            mb={0.5}
                                          >
                                            <Box
                                              width={16}
                                              height={16}
                                              mr={1}
                                              style={{
                                                backgroundColor: item.color || item.fill || '#ccc',
                                                borderRadius: item.type === 'circle' ? '50%' : 0,
                                                border: item.strokeColor
                                                  ? `1px solid ${item.strokeColor}`
                                                  : 'none',
                                              }}
                                            />
                                            <Typography
                                              variant="caption"
                                              style={{ color: theme.palette.common.white }}
                                            >
                                              {item.name ||
                                                item.value ||
                                                item.label ||
                                                item.title ||
                                                'Unknown'}
                                            </Typography>
                                          </Box>
                                        ));
                                      };

                                      // Try different legend data structures
                                      if (layerGroup.legend) {
                                        //console.log('Found layerGroup.legend:', layerGroup.legend);
                                        if (layerGroup.legend.items) {
                                          //console.log('Using layerGroup.legend.items');
                                          return renderLegendItems(layerGroup.legend.items);
                                        }
                                        if (layerGroup.legend.config?.items) {
                                          //console.log('Using layerGroup.legend.config.items');
                                          return renderLegendItems(layerGroup.legend.config.items);
                                        }
                                        if (Array.isArray(layerGroup.legend)) {
                                          console.log('Using layerGroup.legend as array');
                                          return renderLegendItems(layerGroup.legend);
                                        }
                                      }

                                      // Try layer-specific legend data
                                      if (activeLayer?.legend) {
                                        // console.log(
                                        //   'Found activeLayer.legend:',
                                        //   activeLayer.legend
                                        // );
                                        if (activeLayer.legend.items) {
                                          //console.log('Using activeLayer.legend.items');
                                          return renderLegendItems(activeLayer.legend.items);
                                        }
                                        if (Array.isArray(activeLayer.legend)) {
                                          //console.log('Using activeLayer.legend as array');
                                          return renderLegendItems(activeLayer.legend);
                                        }
                                      }

                                      // If no legend data found, show a simple indicator
                                      console.log(
                                        'No legend data found, showing fallback indicator'
                                      );
                                      return (
                                        <Box display="flex" alignItems="center">
                                          <Box
                                            width={16}
                                            height={16}
                                            mr={1}
                                            style={{
                                              backgroundColor:
                                                layerGroup.color || theme.palette.secondary.main,
                                              borderRadius: 2,
                                              border: '1px solid rgba(255,255,255,0.2)',
                                            }}
                                          />
                                          <Typography
                                            variant="caption"
                                            style={{
                                              color: theme.palette.common.white,
                                              fontStyle: 'italic',
                                              fontSize: '11px',
                                            }}
                                          >
                                            Layer active
                                          </Typography>
                                        </Box>
                                      );
                                    })()}
                                  </Collapse>

                                  {/* Show preview for collapsed long legends */}
                                  {/* {hasLongLegend && !isLegendExpanded && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      style={{
                                        color: 'rgba(255,255,255,0.5)',
                                        fontSize: '10px',
                                        fontStyle: 'italic',
                                      }}
                                    >
                                      Click to view {legendItemCount} legend items
                                    </Typography>
                                  </Box>
                                )} */}
                                </Box>
                              </ListItem>

                              {/* Thin separator line between layers (except for the last item) */}
                              {i < layerGroups.length - 1 && (
                                <Box
                                  height="1px"
                                  bgcolor="rgba(255,255,255,0.1)"
                                  mx={0}
                                  my={0.75}
                                />
                              )}
                            </React.Fragment>
                          );
                        })}
                    </List>
                  </Box>
                </Collapse>

                {/* Show message when expanded but no layers at all */}
                <Collapse in={expanded && !hasAnyLayers}>
                  <Box p={2} bgcolor="#455A64">
                    <Typography
                      variant="body2"
                      style={{
                        color: 'rgba(255,255,255,0.6)',
                        textAlign: 'center',
                        fontStyle: 'italic',
                      }}
                    >
                      {t('No layers added')}
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            </animated.div>
          )}
        </LegendWrapper>

        {/* Opacity Popup - Rendered at root level */}
        <OpacityPopup
          open={opacityPopup.open}
          onClose={this.handleOpacityClose}
          opacity={opacityPopup.opacity}
          onOpacityChange={this.handleOpacityChange}
          anchorEl={opacityPopup.anchorEl}
          theme={theme}
        />

        {/* Info Dialog */}
        <Dialog open={infoDialog.open} onClose={this.handleCloseInfoDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{infoDialog.layerGroup?.name}</DialogTitle>
          <DialogContent>
            {infoDialog.layerGroup?.description && (
              <Html html={infoDialog.layerGroup.description} />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseInfoDialog} color="primary">
              {t('Close')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

export default compose(withStyles(styles), withTheme, withTranslation())(LegendComponent);
